export const n = (v) => parseFloat(v) || 0;
export const r2 = (x) => Math.round(x * 100) / 100;

export const Z_STATUS = { draft: "Draft", submitted: "Submitted", rejected: "Returned", approved: "Approved", voided: "Voided" };
export const Z_STATUS_CLS = {
  draft: "bg-navy/5 text-navy/60", submitted: "bg-amber-50 text-amber-700", rejected: "bg-red-50 text-red-700",
  approved: "bg-emerald-50 text-emerald-700", voided: "bg-navy/5 text-navy/40 line-through",
};

export const emptyZ = (storeId, today) => ({
  store_id: storeId || "", trading_date: today, register_ref: "Till 1", source: "manual",
  cash_sales: "", card_mode: "combined", card_sales: "", card_machines: [{ name: "Card machine 1", amount: "" }],
  account_sales: "", bank_transfer_sales: "", other_tenders: [], cash_refunds: "", card_refunds: "", other_refunds: "", refund_layout: "", refunds_printed: "", adjustments: "", z_total: "", notes: "",
});

export const cardTotal = (f) => (f.card_mode === "machines" ? r2(f.card_machines.reduce((s, m) => s + n(m.amount), 0)) : r2(n(f.card_sales)));
export const otherTotal = (f) => r2(f.other_tenders.reduce((s, t) => s + n(t.amount), 0));
export const refundTotal = (f) => r2(n(f.cash_refunds) + n(f.card_refunds) + n(f.other_refunds));
const tenders = (f) => r2(n(f.cash_sales) + cardTotal(f) + n(f.account_sales) + n(f.bank_transfer_sales) + otherTotal(f) + n(f.adjustments));
const totalFor = (f, layout) => r2(tenders(f) - (layout === "net" ? 0 : refundTotal(f)));
const same = (a, b) => Math.abs(a - b) < 0.005;
// gross = tender lines are before refunds; net = tender lines already have refunds taken off. Detected from the printed total.
export const detectedLayout = (f) => {
  if (!refundTotal(f)) return "gross";
  if (f.z_total === "") return null;
  if (same(n(f.z_total), totalFor(f, "gross"))) return "gross";
  return same(n(f.z_total), totalFor(f, "net")) ? "net" : null;
};
export const layoutFor = (f) => (refundTotal(f) ? f.refund_layout || detectedLayout(f) : "gross");
export const computedTotal = (f) => totalFor(f, layoutFor(f) || "gross");
export const totalMismatch = (f) => f.z_total !== "" && !same(n(f.z_total), computedTotal(f));
export const refundSplitMismatch = (f) => f.refunds_printed !== "" && !same(n(f.refunds_printed), refundTotal(f));

export const zPayload = (f, docs, ai, submit, confirmed) => ({
  store_id: parseInt(f.store_id), trading_date: f.trading_date, register_ref: f.register_ref, source: f.source,
  cash_sales: n(f.cash_sales), card_sales: f.card_mode === "combined" ? n(f.card_sales) : null,
  card_machines: f.card_mode === "machines" ? f.card_machines.filter((m) => m.name.trim() || n(m.amount)).map((m) => ({ name: m.name.trim() || "Card machine", amount: n(m.amount) })) : [],
  account_sales: n(f.account_sales), bank_transfer_sales: n(f.bank_transfer_sales),
  other_tenders: f.other_tenders.filter((t) => n(t.amount)).map((t) => ({ name: t.name.trim() || "Other", amount: n(t.amount) })),
  cash_refunds: n(f.cash_refunds), card_refunds: n(f.card_refunds), other_refunds: n(f.other_refunds), refund_layout: layoutFor(f),
  adjustments: n(f.adjustments), total_sales: f.z_total === "" ? computedTotal(f) : n(f.z_total),
  ai_values: ai || null, notes: f.notes, document_ids: f.source === "upload" ? docs.map((d) => d.id) : [], submit, confirmed,
});

const s = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

export const fromOcr = (p, d) => {
  const machines = (d.card_machines || []).filter((m) => m && m.amount);
  return {
    ...p,
    trading_date: d.trading_date || p.trading_date,
    register_ref: d.register_ref || p.register_ref,
    cash_sales: s(d.cash_sales) || p.cash_sales,
    card_mode: machines.length > 1 ? "machines" : "combined",
    card_sales: machines.length > 1 ? p.card_sales : s(d.card_total) || p.card_sales,
    card_machines: machines.length > 1 ? machines.map((m) => ({ name: m.name || "Card machine", amount: s(m.amount) })) : p.card_machines,
    account_sales: s(d.account_sales) || p.account_sales,
    bank_transfer_sales: s(d.bank_transfer_sales) || p.bank_transfer_sales,
    other_tenders: (d.other_tenders || []).filter((t) => t && t.amount).map((t) => ({ name: t.name || "Other", amount: s(t.amount) })),
    // Use the Z report's refund split when it prints one; a single total is never assumed to be cash
    cash_refunds: s(d.cash_refunds) || p.cash_refunds, card_refunds: s(d.card_refunds) || p.card_refunds,
    other_refunds: s(d.other_refunds) || p.other_refunds,
    refunds_printed: d.refunds ? s(Math.abs(d.refunds)) : p.refunds_printed,
    adjustments: s(d.adjustments) || p.adjustments,
    z_total: s(d.total_sales) || p.z_total,
  };
};

export const fromZ = (z, toUK) => ({
  store_id: String(z.store_id), trading_date: toUK(z.trading_date), register_ref: z.register_ref, source: z.source,
  cash_sales: s(z.cash_sales), card_mode: z.card_machines?.length ? "machines" : "combined", card_sales: s(z.card_sales),
  card_machines: z.card_machines?.length ? z.card_machines.map((m) => ({ name: m.name, amount: s(m.amount) })) : [{ name: "Card machine 1", amount: "" }],
  account_sales: s(z.account_sales), bank_transfer_sales: s(z.bank_transfer_sales),
  other_tenders: (z.other_tenders || []).map((t) => ({ name: t.name, amount: s(t.amount) })),
  cash_refunds: s(z.cash_refunds), card_refunds: s(z.card_refunds), other_refunds: s(z.other_refunds),
  refund_layout: z.refunds ? z.refund_layout : "", refunds_printed: "",
  adjustments: s(z.adjustments), z_total: s(z.total_sales), notes: z.notes || "",
});
